/**
 * What a step card says about its blocks, and the one change its rows make —
 * Story 20.10.
 *
 * DECISION-076: "the summary panel is what a **collapsed** step looks like. The
 * **selected** step expands to its block rows." It held the build until a
 * drawing settled the summary's rule, and the 2026-09-17 round-trip drew one
 * (`design/screen-manifest.md` → `editor-build`). DECISION-153 made a row's
 * handle reorder the step's blocks.
 *
 * Pure, so `packages/app/test/step-card.test.ts` can hold the RULE rather than
 * one card: the drawing shows five summaries, and a function written to
 * reproduce five strings can pass on all five and be wrong on the sixth.
 *
 * NO REACT AND NO DRAG LIBRARY. The move is a splice rather than `arrayMove`,
 * so this module loads in node.
 */
import type { Block, Funnel } from '@leadgible/schema'
import { BLOCK_LABEL, hasOptions } from './blocks'
import { DRAWN } from './proposal-copy'
import { criterionFor } from './rubric'

/**
 * A block's own words — what a respondent reads first — or null when it has
 * none of its own: a divider, a rich text, a hidden field.
 *
 * The outcome block is a placement marker; what it renders lives in
 * `outcomes[]`, one per band. The first outcome's heading is what the drawing's
 * Result card shows (`You're a good fit.`).
 */
export function wordsOf(funnel: Funnel, block: Block): string | null {
  if ('question' in block && typeof block.question === 'string') return block.question
  if (block.type === 'intro') return block.heading
  if (block.type === 'outcome') return funnel.outcomes[0]?.heading ?? null
  return null
}

/** The collapsed card's preview line: the first block's words, or its type. */
export function previewOf(funnel: Funnel, block: Block): string {
  return wordsOf(funnel, block) ?? BLOCK_LABEL[block.type]
}

const plural = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`

/**
 * The line under the preview, by the one rule the drawing follows on every card.
 *
 * One block names its type and its detail, and the only details drawn are the
 * option count and the criterion's `out of {n}` — `Card select · 4 options · out
 * of 25`, `Outcome`. The `out of` is the weight the inspector's `Score out of`
 * shows, and it appears only where a criterion scores the block, which is the
 * same test the `Scored` chip makes.
 *
 * Several blocks list every block's type in order, in sentence case —
 * `Intro, rich text`, `Email, name, phone`. The export's first capture read
 * `Intro` for a step holding two blocks, which is the disagreement DECISION-076
 * refused to build a summariser from; the redraw lists both.
 *
 * Null for a step with no blocks, which keeps its own line.
 */
export function summaryOf(funnel: Funnel, blocks: readonly Block[]): string | null {
  const [first, ...rest] = blocks
  if (first === undefined) return null
  if (rest.length > 0) {
    return blocks.map((block, i) => (i === 0 ? BLOCK_LABEL[block.type] : BLOCK_LABEL[block.type].toLowerCase())).join(', ')
  }
  const parts = [BLOCK_LABEL[first.type]]
  if (hasOptions(first.type) && 'options' in first) parts.push(plural(first.options.length, 'option'))
  const criterion = criterionFor(funnel, first.id)
  if (criterion !== undefined) parts.push(`out of ${criterion.weight}`)
  return parts.join(' · ')
}

/**
 * The proposal review's line (Story 27.4): the collapsed card's own, and for one text question
 * the AI reads, how many facts it pulls out — `Long text · pulls out 2 facts`, which only the
 * proposal's drawing shows. The builder's cards keep `summaryOf` as their own drawing draws it.
 */
export function proposedSummaryOf(funnel: Funnel, blocks: readonly Block[]): string | null {
  const line = summaryOf(funnel, blocks)
  const [only, ...more] = blocks
  if (line === null || only === undefined || more.length > 0) return line
  const facts = funnel.rubric.extraction.filter((field) => field.block_id === only.id).length
  return facts === 0 ? line : `${line} · ${DRAWN.pullsOut(facts)}`
}

/**
 * Move one block within its step — DECISION-153.
 *
 * The step's `blocks` order is the only thing written. No block, id, field key,
 * criterion or logic rule changes, because none of them keys on a block's
 * position in its step.
 *
 * Writes nothing and returns false when either id is not in that step, so a
 * drop can never carry a block into another one: moving between steps is
 * deliberately not built (nothing draws a drop onto a collapsed card).
 */
export function moveBlockInStep(draft: Funnel, stepId: string, activeId: string, overId: string): boolean {
  const step = draft.steps.find((s) => s.id === stepId)
  if (step === undefined) return false
  const from = step.blocks.indexOf(activeId)
  const to = step.blocks.indexOf(overId)
  if (from < 0 || to < 0 || from === to) return false
  const [moved] = step.blocks.splice(from, 1)
  step.blocks.splice(to, 0, moved!)
  return true
}
